import { inject, Injectable } from "@angular/core";
import { isODataResult } from "@tylertech/odata-module";
import { ClassMetadata, RepositoryDataCollection, RepositoryGet, Tuple, } from "@tylertech/tyler-api-types";
import { CacheHelperService } from "./cacheHelperService";
import { RepositoryService } from "./repositoryService.g";
import { ApiService } from "./apiService";
import { SecurityService } from "./security.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export interface MetadataObject
{
    $$TypeName?: string;
}

// FIXME(DO-4549) - Move to library/metadata
@Injectable()
export class CachedRepositoryService
    extends RepositoryService
{
    constructor(
        private readonly $cacheHelperService: CacheHelperService
    )
    {
        super(inject(ApiService));

        const securityService = inject(SecurityService);
        securityService.principleChange
            .pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                if (securityService.isAuthenticated)
                {
                    return;
                }

                this.$cacheHelperService.clearAll();
            });

    }

    private AddTypeOnArray<T extends MetadataObject = MetadataObject>(array: Array<T>, type: string, filter: string): Promise<Array<T>>
    {
        if (array && !filter?.includes("$select"))
        {
            if (isODataResult(array))
            {
                array.value.forEach(e =>
                {
                    this.AddTypeOnObject(e, type);
                });
            }
            else
            {
                array.forEach(e =>
                {
                    this.AddTypeOnObject(e, type);
                });
            }
        }

        return Promise.resolve(array);
    }

    private AddTypeOnObject<T extends MetadataObject = MetadataObject>(object: T, type: string): Promise<T>
    {
        if (!object.$$TypeName)
        {
            object.$$TypeName = type;
        }

        return Promise.resolve(object);
    }

    ClearAll(): void
    {
        this.$cacheHelperService.clearAll();
    }

    ClearFormCache(dataCollection: RepositoryDataCollection): void
    {
        this.$cacheHelperService.clearAllForType(dataCollection.Entity.$$TypeName);
        const otherEntities = Object.keys(dataCollection.OtherEntities);
        otherEntities.forEach(type =>
        {
            return this.$cacheHelperService.clearAllForType(type);
        });
    }

    IsInCache(type: string, action: string, filterString?: string): boolean
    {
        return this.$cacheHelperService.isInCache(
            `${CachedRepositoryService.name}.${action}`,
            {
                Item1: type,
                Item2: filterString,
            });
    }

    FetchCache(type: string, filterString?: string, ignoreCache?: boolean): Promise<Array<unknown>>
    {
        if (ignoreCache)
        {
            return super.Fetch(type, filterString)
                .then(r =>
                {
                    return this.AddTypeOnArray(r, type, filterString);
                });
        }

        return this.Fetch(type, filterString);
    }

    override Copy(collection: RepositoryDataCollection): Promise<unknown>
    {
        return super.Copy(collection)
            .then(r =>
            {
                return this.AddTypeOnObject(r, collection.Type);
            });
    }

    override Fetch(type: string, filterString?: string): Promise<Array<unknown>>
    {
        return this.$cacheHelperService.routeThroughCache(
            `${CachedRepositoryService.name}.Fetch`,
            {
                Item1: type,
                Item2: filterString,
            },
            x => super.Fetch(x.Item1, x.Item2)
                .then(r =>
                {
                    return this.AddTypeOnArray(r, type, filterString) as unknown as Array<unknown>;
                })
        );
    }

    override FetchWithQuestions(typeAndQuestions: Tuple<string, Record<string, boolean>>, filterString?: string): Promise<Array<unknown>>
    {
        return this.$cacheHelperService.routeThroughCache(
            `${CachedRepositoryService.name}.Fetch`,
            {
                Item1: typeAndQuestions.Item1,
                Item2: filterString,
            },
            (_params: Tuple<string, string>) =>
            {
                return super.FetchWithQuestions(typeAndQuestions, filterString)
                    .then(r =>
                    {
                        return this.AddTypeOnArray(r, typeAndQuestions.Item1, filterString);
                    });
            }
        );
    }

    override Get(typeAndId: Tuple<string, string>): Promise<unknown>
    {
        return this.$cacheHelperService.routeThroughCache(
            `${CachedRepositoryService.name}.Get`,
            typeAndId,
            (params: Tuple<string, string>) =>
            {
                return super.Get(params)
                    .then(r =>
                    {
                        return this.AddTypeOnObject(r, typeAndId.Item1);
                    });
            }
        );
    }

    override GetMetadata(typeAndString: Tuple<string, string>): Promise<ClassMetadata>
    {
        return super.GetMetadata(typeAndString);
    }

    override GetNew(typeAndString: Tuple<string, string>, filterString?: string): Promise<unknown>
    {
        return super.GetNew(typeAndString, filterString)
            .then(r =>
            {
                return this.AddTypeOnObject(r, typeAndString.Item1);
            });
    }

    override GetWithQuestions(get: RepositoryGet, filter: string): Promise<unknown>
    {
        return this.$cacheHelperService.routeThroughCache(
            `${CachedRepositoryService.name}.Get`,
            {
                Item1: get.Type,
                Item2: get.Id
            },
            (_params: Tuple<string, string>) =>
            {
                return super.GetWithQuestions(get, filter)
                    .then(r =>
                    {
                        return this.AddTypeOnObject(r, get.Type);
                    });
            }
        );
    }

    override Save(typeAndJsonString: Tuple<string, string>): Promise<unknown>
    {
        return super.Save(typeAndJsonString)
            .then(response =>
            {
                this.$cacheHelperService.clearAllForType(typeAndJsonString.Item1);
                return response;
            });
    }

    override SaveAll(collection: RepositoryDataCollection): Promise<RepositoryDataCollection>
    {
        return super.SaveAll(collection)
            .then(response =>
            {
                this.$cacheHelperService.clearAll();
                return response;
            });
    }
}
